/**
 * Client-side placeholder extraction.
 *
 * Mirrors the backend HasPlaceholder logic for all 37 component types so that
 * placeholder lists can be computed in-memory from rawComponentData + graph
 * edges, eliminating the per-component REST call.
 */

// ---------------------------------------------------------------------------
// toSlug – port of backend Util.toSlug
// ---------------------------------------------------------------------------
function toSlug(input) {
  if (input == null) return '';
  const str = String(input);
  let slug = str.replace(/\s/g, '_');
  slug = slug.normalize('NFD');
  slug = slug.replace(/[^\w-]/g, '');
  slug = slug.replace(/(^-|-$)/g, '');
  return slug;
}

// ---------------------------------------------------------------------------
// Form helpers
// ---------------------------------------------------------------------------

const FORM_EXTRA_PLACEHOLDERS = [
  { key: 'projectSessionId', type: 'text' },
  { key: 'formSessionId', type: 'text' },
  { key: 'formSubmittedDate', type: 'datetime' },
  { key: 'formSessionURL', type: 'text' },
  { key: 'allQuestionsAnswers.html', type: 'html' },
  { key: 'allQuestionsAnswers.text', type: 'text' },
];

function getAnswerFormat(pageComponent) {
  try {
    // v2 stores type at field.type; v1 uses views.answer.view.format
    return pageComponent?.type ?? pageComponent?.views?.answer?.view?.format ?? 'raw_text';
  } catch {
    return 'raw_text';
  }
}

function getPlaceholderType(pageComponent) {
  const format = getAnswerFormat(pageComponent);
  switch (format) {
    case 'number_input':  // v1
    case 'number':        // v2
      return 'number';
    case 'list_input':       // v1
    case 'list':             // v2
    case 'custom_map':
    case 'multiple_choice':  // v1
    case 'checkbox':         // v2
    case 'upload_media':     // v1
    case 'file_upload':      // v2
      return 'list';
    case 'calendar':
      return 'datetime';
    case 'rich_text_editor':
    case 'row_text':
      return 'html';
    case 'free_text':         // v1
    case 'long_text':         // v2
    case 'single_line_text':  // v1
    case 'short_text':        // v2
    case 'single_choice':     // v1
    case 'radio_button':      // v2
    default:
      return 'text';
  }
}

function getColumnsFromTable(pageComponent) {
  try {
    return pageComponent?.views?.answer?.view?.data?.default ?? [];
  } catch {
    return [];
  }
}

function getRowLabels(pageComponent) {
  try {
    return pageComponent?.views?.answer?.view?.data?.rowLabels ?? [];
  } catch {
    return [];
  }
}

function mapDynamicFieldTypeToPlaceholderType(fieldType) {
  if (fieldType == null) return 'text';
  switch (fieldType) {
    case 'number':
      return 'number';
    case 'date':
      return 'datetime';
    case 'checkbox':
    case 'list':
    case 'repeatable_section':
      return 'list';
    case 'rich_text':
      return 'html';
    default:
      return 'text';
  }
}

function extractFormPlaceholders(rawComponentData) {
  const result = [];
  const isV2 = rawComponentData?.schemaVersion >= 2 || Array.isArray(rawComponentData?.pages);
  const data = isV2 ? rawComponentData : rawComponentData?.data;
  if (!data) return result;

  // prefixLabel may be at rawComponentData level or data level
  let prefixLabel = rawComponentData.prefixLabel || data.prefixLabel || null;
  if (prefixLabel && typeof prefixLabel === 'string' && prefixLabel.trim() === '') {
    prefixLabel = null;
  }

  const pages = data.pages;
  if (!pages || typeof pages !== 'object') return result;

  // v2 pages is an array; v1 pages is an object keyed by index
  const pageEntries = Array.isArray(pages) ? pages : Object.values(pages);
  for (const page of pageEntries) {
    const pageComponents = page?.fields ?? page?.components;
    if (!Array.isArray(pageComponents)) continue;

    for (const pc of pageComponents) {
      let pcKey = pc?.key != null ? String(pc.key) : null;
      if (pcKey == null) continue;

      if (prefixLabel) {
        pcKey = prefixLabel + '.' + pcKey;
      }

      const ptype = getPlaceholderType(pc);
      const answerFormat = getAnswerFormat(pc);

      if (answerFormat === 'table') {
        const exportType = pc.exportType;
        if (exportType === 'columnsAsLists') {
          for (const col of getColumnsFromTable(pc)) {
            const colName = col?.columnName;
            if (colName == null) continue;
            const colKey = toSlug(colName).toLowerCase();
            result.push({ key: pcKey + '.answer.' + colKey, type: 'list' });
          }
        } else if (exportType === 'rowsAsLists') {
          for (const row of getRowLabels(pc)) {
            const label = row?.rowLabel;
            const rowKey = row?.rowKey;
            let finalKey;
            if (label && label.trim() !== '') {
              finalKey = pcKey + '.answer.' + toSlug(label).toLowerCase();
            } else {
              finalKey = pcKey + '.answer.' + (rowKey ?? '');
            }
            result.push({ key: finalKey, type: 'list' });
          }
        }
      } else if (answerFormat === 'e_signature') {
        result.push({ key: pcKey + '.answer.url', type: ptype });
        result.push({ key: pcKey + '.answer.html', type: 'html' });
      } else if (answerFormat === 'image_annotation') {
        result.push({ key: pcKey + '.answer', type: 'text' });
      } else if (answerFormat === 'file_preview' || answerFormat === 'video_embed') {
        // skip – no placeholder
      } else {
        result.push({ key: pcKey + '.answer', type: ptype });
      }

      // comment
      if (pc.comment === true) {
        result.push({ key: pcKey + '.comment', type: 'text' });
      }
    }
  }

  // Extra placeholders
  for (const extra of FORM_EXTRA_PLACEHOLDERS) {
    result.push({
      key: prefixLabel ? prefixLabel + '.' + extra.key : extra.key,
      type: extra.type,
    });
  }

  return result;
}

// ---------------------------------------------------------------------------
// extractPlaceholders – main switch on component type
// ---------------------------------------------------------------------------

/**
 * Extract the placeholder keys that a single component exports.
 *
 * @param {string} type - component type (e.g. 'form', 'code')
 * @param {object} rawComponentData - the rawComponentData blob from GraphQL
 * @returns {Array<{key:string, type:string}>}
 */
export function extractPlaceholders(type, rawComponentData) {
  if (!rawComponentData) return [];

  // V2 schema flattens fields onto rawComponentData; V1 nests under .data
  const isV2 = rawComponentData.schemaVersion >= 2;
  const data = isV2 ? rawComponentData : rawComponentData.data;

  switch (type) {
    // -----------------------------------------------------------------------
    // Simple single-key types (slugify key)
    // -----------------------------------------------------------------------
    case 'document_editor':
    case 'create_file':
    case 'create_database_record':
    case 'update_database_record': {
      const key = data?.outputKey ?? data?.key;
      return key != null ? [{ key: toSlug(String(key)), type: 'text' }] : [];
    }

    // Single-key types using outputKey (v2) / outputPlaceholderKey (v1)
    case 'trigger_on_database_update':
    case 'trigger_from_external_app':
    case 'await_external_event': {
      const key = data?.outputKey ?? data?.outputPlaceholderKey;
      return key != null ? [{ key: String(key), type: type === 'await_external_event' ? 'json' : 'text' }] : [];
    }

    case 'trigger_from_api_polling': {
      const key = data?.outputKey ?? data?.outputPlaceholderKey;
      return key != null ? [{ key: String(key), type: 'json' }] : [];
    }

    case 'perform_action_in_external_app': {
      const key = data?.outputKey ?? data?.outputPlaceholderKey;
      return key != null ? [{ key: String(key), type: 'json' }] : [];
    }

    case 'converge_threads': {
      const key = data?.outputKey ?? data?.outputPlaceholderKey;
      return key != null ? [{ key: String(key), type: 'json' }] : [];
    }

    case 'webhook_trigger':
    case 'webhook_trigger_v2': {
      const key = data?.outputKey ?? data?.outputPlaceholderKey;
      return key != null ? [{ key: String(key), type: 'json' }] : [];
    }

    case 'http_request': {
      const key = data?.outputKey ?? data?.responseOutputPlaceholder;
      return key != null ? [{ key: String(key), type: 'json' }] : [];
    }

    // schedule_start / time_delay: outputKey (v2) / key (v1), datetime, no slugify
    case 'schedule_start':
    case 'time_delay': {
      const key = data?.outputKey ?? data?.key;
      return key != null ? [{ key: String(key), type: 'datetime' }] : [];
    }

    // V2 table types
    case 'create_row_v2': {
      const key = data?.outputPlaceholder ?? 'new_row_id';
      return key ? [{ key: String(key), type: 'text' }] : [];
    }

    case 'query_table_v2': {
      const queries = data?.queries;
      if (!Array.isArray(queries)) return [];
      return queries
        .filter(q => q?.outputPlaceholder != null)
        .map(q => ({
          key: String(q.outputPlaceholder),
          type: q.outputFormat === 'string' ? 'text' : 'list',
        }));
    }

    case 'trigger_table_v2': {
      let key = data?.outputKey;
      if (typeof key === 'string' && key.trim() === '') {
        key = null;
      }
      if (key == null) {
        key = 'trigger_table_v2_output';
      }
      return [{ key: String(key), type: 'text' }];
    }

    // update_row_v2 produces no output placeholders

    // Types that export nothing
    case 'email':
    case 'trigger_another_workflow':
    case 'slack':
    case 'api_export':
    case 'conditional_workflow_path':
      return [];

    // -----------------------------------------------------------------------
    // Array-based types (iterate data or data sub-field)
    // -----------------------------------------------------------------------
    case 'webhook':
    case 'trigger_from_zapier': {
      const items = data?.samplePlaceholders;
      if (!Array.isArray(items)) return [];
      return items
        .filter(item => item?.key != null)
        .map(item => ({ key: toSlug(String(item.key)), type: 'text' }));
    }

    case 'conditional_workflow':
    case 'conditional_logic': {
      // V2 conditional_workflow wraps in { conditions: [...] }; conditional_logic uses raw array
      const conditions = Array.isArray(data) ? data : Array.isArray(data?.conditions) ? data.conditions : [];
      return conditions
        .filter(item => item?.key != null)
        .map(item => ({ key: toSlug(String(item.key)), type: 'text' }));
    }

    case 'query_database_record':
    case 'data_bank': {
      const queryData = data?.queries ?? data?.queryData;
      if (!Array.isArray(queryData)) return [];
      return queryData
        .filter(item => item?.key != null)
        .map(item => ({
          key: toSlug(String(item.key)),
          type: item.returnType === 'string' ? 'text' : 'list',
        }));
    }

    case 'session_variables': {
      const vars = data?.variables ?? data?.sessionVariableData;
      if (!Array.isArray(vars)) return [];
      return vars
        .filter(item => item?.key != null)
        .map(item => ({ key: toSlug(String(item.key)), type: toSlug(String(item.type ?? 'text')) }));
    }

    case 'loop_through_list': {
      // V2 schema: flat object with iteratorKey
      if (!Array.isArray(data)) {
        const iteratorKey = data?.iteratorKey ?? data?.key;
        return iteratorKey != null ? [{ key: String(iteratorKey), type: 'text' }] : [];
      }
      // V1 schema: array of {key, list, ...}
      return data
        .filter(item => item?.key != null)
        .map(item => ({ key: String(item.key), type: 'text' }));
    }

    case 'parse_json': {
      const samplePlaceholders = data?.samplePlaceholders;
      if (!Array.isArray(samplePlaceholders)) return [];
      return samplePlaceholders
        .filter(item => {
          const checked = item?.checked;
          return (checked === true || checked === 'true') && item?.key != null;
        })
        .map(item => ({ key: toSlug(String(item.key)), type: 'text' }));
    }

    case 'code':
    case 'advanced_code': {
      const placeholders = data?.placeholders;
      if (!Array.isArray(placeholders)) return [];
      return placeholders
        .filter(item => item?.key != null)
        .map(item => ({ key: toSlug(String(item.key)), type: toSlug(String(item.type ?? 'text')) }));
    }

    case 'custom_function': {
      // Mirrors Code/advanced_code: instance state snapshots the bound
      // function's outputSchema into componentData.placeholders as
      // CodePlaceholder shapes. useCustomFunctionInstanceStore keeps
      // this array in sync on function pick.
      const placeholders = data?.placeholders;
      if (!Array.isArray(placeholders)) return [];
      return placeholders
        .filter(item => item?.key != null)
        .map(item => ({ key: toSlug(String(item.key)), type: toSlug(String(item.type ?? 'text')) }));
    }

    case 'calculator': {
      if (!Array.isArray(data)) return [];
      return data
        .filter(item => item?.key != null)
        .map(item => {
          const perform = item.perform;
          const dtFunc = item.datetimefunc;
          const pType =
            perform === 'DATETIME' && dtFunc === 'ADDSUBS' ? 'datetime' : 'number';
          return { key: String(item.key), type: pType };
        });
    }

    // -----------------------------------------------------------------------
    // Complex types
    // -----------------------------------------------------------------------
    case 'list_transform': {
      if (!Array.isArray(data)) return [];
      const result = [];
      for (const field of data) {
        if (field?.func == null) continue;
        const func = String(field.func);
        const normalizedFunc = {
          split_list_into_placeholders: 'SPLIT',
          convert_list_to_text: 'JOIN',
          join: 'JOIN',
        }[func] || func;

        if (normalizedFunc === 'SPLIT') {
          if (field.key != null) {
            result.push({ key: toSlug(String(field.key)), type: 'list' });
          }
          const numPlaceholder = parseInt(field.numPlaceholder, 10) || 0;
          const exportPlaceholders = field.exportPlaceholders;
          if (!Array.isArray(exportPlaceholders)) continue;
          for (let i = 0; i < numPlaceholder && i < exportPlaceholders.length; i++) {
            const ep = exportPlaceholders[i];
            if (ep?.key == null) continue;
            result.push({ key: toSlug(String(ep.key)), type: 'text' });
          }
        } else {
          if (field.key == null) continue;
          let pType = toSlug(String(field.outputType ?? 'text'));
          if (normalizedFunc === 'JOIN') {
            pType = 'text';
          }
          result.push({ key: toSlug(String(field.key)), type: pType });
        }
      }
      return result;
    }

    case 'form':
    case 'sequential_form':
    case 'form_section':
      return extractFormPlaceholders(rawComponentData);

    case 'dynamic_form':
    case 'dynamic_review_task':
    case 'form_step': {
      // v2 stores fields at componentData root; v1 stores under componentData.data
      const dynFields = Array.isArray(rawComponentData?.fields)
        ? rawComponentData.fields
        : Array.isArray(data?.fields)
          ? data.fields
          : [];
      if (!Array.isArray(dynFields) || dynFields.length === 0) return [];

      const result = [];
      for (const field of dynFields) {
        const key = field?.key != null ? String(field.key) : '';
        const type = field?.type;
        if (key.trim() === '' || type === 'display') continue;

        result.push({ key, type: mapDynamicFieldTypeToPlaceholderType(type) });

        // Keep parity with backend HasPlaceholder logic for repeatable sections.
        if (type === 'repeatable_section' && Array.isArray(field?.children)) {
          for (const child of field.children) {
            const childKey = child?.key != null ? String(child.key) : '';
            if (childKey.trim() === '') continue;
            result.push({
              key: `${key}.${childKey}`,
              type: mapDynamicFieldTypeToPlaceholderType(child?.type),
            });
          }
        }
      }
      return result;
    }

    case 'artifact':
      return [
        { key: 'artifact.submitted.artifactInstanceId', type: 'text' },
        { key: 'artifact.submitted.eventName', type: 'text' },
        { key: 'artifact.submitted.payload', type: 'json' },
        { key: 'artifact.submitted.actor', type: 'json' },
        { key: 'artifact.submitted.submittedAt', type: 'datetime' },
        { key: 'artifactInstanceId', type: 'text' },
        { key: 'event.name', type: 'text' },
        { key: 'event.payload', type: 'json' },
        { key: 'event.actor.email', type: 'text' },
      ];

    case 'api_request_and_response': {
      // Backend saves requestData/responseData as siblings of data in componentData.
      // Support both paths: data.requestData (normalized) and rawComponentData.requestData (legacy).
      const requestData = data?.requestData ?? rawComponentData?.requestData;
      const skipSample = requestData?.skipSample === true;
      if (skipSample) {
        const rawKey = requestData?.rawResponsePlaceholderName;
        return rawKey != null ? [{ key: String(rawKey), type: 'json' }] : [];
      }
      const responseData = data?.responseData ?? rawComponentData?.responseData;
      const fields = responseData?.placeholders;
      if (!Array.isArray(fields)) return [];
      return fields
        .filter(f => f?.key != null)
        .map(f => ({ key: toSlug(String(f.key)), type: 'text' }));
    }

    case 'trigger_from_a_workflow': {
      const connectionPlaceholders = data?.connectionData?.placeholders;
      if (!Array.isArray(connectionPlaceholders)) return [];
      return connectionPlaceholders
        .filter(p => p?.targetKey != null && p?.type != null)
        .map(p => ({ key: String(p.targetKey), type: String(p.type) }));
    }

    case 'trigger_a_zap': {
      const key = data?.key ?? data?.outputKey;
      return key != null ? [{ key: String(key), type: 'text' }] : [];
    }

    case 'trigger_via_email': {
      const groups = data?.outputPlaceholders ?? data?.incomingMessagePlaceholders;
      if (!groups || typeof groups !== 'object') return [];
      const allItems = [
        ...(groups.content ?? []),
        ...(groups.addresses ?? []),
        ...(groups.metadata ?? []),
      ];
      return allItems
        .filter(item => item?.selected && item?.key != null)
        .map(item => ({ key: String(item.key), type: String(item.type ?? 'text') }));
    }

    case 'ai_assistant': {
      const outputPlaceholders = data?.aiAssistantData?.outputPlaceholders;
      if (!Array.isArray(outputPlaceholders)) return [];
      return outputPlaceholders
        .filter(p => p?.key != null)
        .map(p => ({ key: toSlug(String(p.key)), type: toSlug(String(p.type ?? 'text')) }));
    }

    case 'ai_agent': {
      // v2 flattens aiAgentData to root data level; v1 nests under data.aiAgentData
      const outputPlaceholders = data?.outputPlaceholders ?? data?.aiAgentData?.outputPlaceholders;
      if (!Array.isArray(outputPlaceholders)) return [];
      return outputPlaceholders
        .filter(p => p?.key != null)
        .map(p => ({ key: toSlug(String(p.key)), type: toSlug(String(p.type ?? 'text')) }));
    }

    case 'bulk_assessment': {
      const baseKey = data?.outputKey ?? data?.key;
      const fields = data?.fields;
      if (baseKey == null || !Array.isArray(fields)) return [];
      return fields
        .filter(f => f?.keyName != null)
        .map(f => ({
          key: String(baseKey) + '.' + toSlug(String(f.keyName)),
          type: String(f.dataType ?? 'text'),
        }));
    }

    default:
      return [];
  }
}

// ---------------------------------------------------------------------------
// Graph traversal – find upstream components
// ---------------------------------------------------------------------------

/**
 * Build a set of all component IDs that are upstream of targetId
 * (i.e. can reach targetId via nextComponents edges). Includes the target
 * itself so its own outputs can be used as self-referencing placeholders
 * (this mirrors the backend behaviour).
 *
 * @param {string} targetId
 * @param {Array} components - project store components array
 * @returns {string[]} array of upstream component IDs (including targetId)
 */
export function getUpstreamComponentIds(targetId, components) {
  if (!components || !targetId) return [];

  // Build reverse adjacency: childId -> Set<parentId>
  const reverseAdj = {};
  for (const comp of components) {
    const id = comp.componentId;
    if (!reverseAdj[id]) reverseAdj[id] = new Set();

    const nexts = comp.nextComponents;
    if (!Array.isArray(nexts)) continue;
    for (const nc of nexts) {
      const childId = nc?.componentId;
      if (!childId) continue;
      if (!reverseAdj[childId]) reverseAdj[childId] = new Set();
      reverseAdj[childId].add(id);
    }
  }

  // BFS upstream from target
  const visited = new Set();
  const queue = [targetId];
  visited.add(targetId);

  while (queue.length > 0) {
    const current = queue.shift();
    const parents = reverseAdj[current];
    if (!parents) continue;
    for (const parentId of parents) {
      if (!visited.has(parentId)) {
        visited.add(parentId);
        queue.push(parentId);
      }
    }
  }

  visited.delete(targetId);
  return Array.from(visited);
}

// ---------------------------------------------------------------------------
// Main entry point
// ---------------------------------------------------------------------------

const TYPES_REQUIRING_SECRETS = new Set([
  'api_request_and_response',
  'api_export',
  'code',
  'advanced_code',
  'trigger_from_api_polling',
  'await_external_event',
  'http_request',
]);

/**
 * Creation-edge placeholder defaults. Mirrors
 * `CreationPlaceholderConfig.DEFAULTS` on the engine — keep them in
 * lockstep. The `systemOutput` is the canonical discriminator (the engine
 * and frontend agree on this string regardless of how the author renamed
 * the visible key); the `defaultKey` is the bare fallback used for legacy
 * components that pre-date the rename UI.
 *
 * When a target node is reached via a `triggerOn=CREATION` edge from one
 * of these source types, the source's regular `extractPlaceholders`
 * output is replaced by the renameable creation-time placeholder (see
 * {@link getCreationPlaceholdersForComponent}). The source is still
 * WAITING and its normal outputs haven't resolved yet, so emitting them
 * would leak unresolved `${...}` strings.
 *
 * The runtime values for these keys are stamped onto componentData by the
 * source's own WAITING handler (see `DynamicReviewTaskService`,
 * `AiAgentConsumer`, `FormStepService`), so they're available the instant
 * the creation child fires.
 */
const CREATION_PLACEHOLDER_DEFAULTS_BY_TYPE = {
  dynamic_review_task: { systemOutput: 'taskUrl', defaultKey: 'taskUrl', type: 'text' },
  ai_agent: { systemOutput: 'agentSessionUrl', defaultKey: 'agentSessionUrl', type: 'text' },
  form_step: { systemOutput: 'formUrl', defaultKey: 'formUrl', type: 'text' },
  artifact: { systemOutput: 'artifactUrl', defaultKey: 'artifactUrl', type: 'text' },
};

/**
 * Resolve the user-renameable creation-edge placeholder for a single
 * source component, falling back to the system default when nothing is
 * stored on componentData. Reads the same shape the engine resolves
 * (CreationPlaceholderConfig).
 *
 * @returns {Array<{key:string, type:string}>} singleton list, or [] when
 *   the type is not creation-port-aware.
 */
export function getCreationPlaceholdersForComponent(comp) {
  const defaults = CREATION_PLACEHOLDER_DEFAULTS_BY_TYPE[comp?.type];
  if (!defaults) return [];

  const raw = comp?.rawComponentData;
  // v2 schema is flat at rawComponentData root; v1 nests under .data.
  // For ai_agent, the entry lives inside aiAgentData.outputPlaceholders
  // (the existing systemOutput-discriminated array); for review_task /
  // form_step it lives at root.creationOutputPlaceholder.
  let key = null;
  if (comp.type === 'ai_agent') {
    const isV2 = raw?.schemaVersion >= 2;
    const data = isV2 ? raw : raw?.data;
    const arr =
      data?.outputPlaceholders ?? data?.aiAgentData?.outputPlaceholders ?? null;
    if (Array.isArray(arr)) {
      const match = arr.find(
        p => String(p?.systemOutput ?? '').trim() === defaults.systemOutput
      );
      if (match?.key && String(match.key).trim()) {
        key = String(match.key).trim();
      }
    }
  } else {
    const isV2 = raw?.schemaVersion >= 2;
    const data = isV2 ? raw : raw?.data;
    const cop = data?.creationOutputPlaceholder;
    if (cop?.key && String(cop.key).trim()) {
      key = String(cop.key).trim();
    }
    // v1 tolerance: also peek at rawComponentData.data.* if data was the v2 root
    if (!key && raw?.data?.creationOutputPlaceholder?.key) {
      const v1Key = String(raw.data.creationOutputPlaceholder.key).trim();
      if (v1Key) key = v1Key;
    }
  }

  return [{ key: key || defaults.defaultKey, type: defaults.type }];
}

// Extracted helper so the edge-aware walk can reuse the same metadata-stamp
// rules without diverging. Returns a fresh array; callers may push to it.
function extractedWithMetadata(comp) {
  const extracted = extractPlaceholders(comp.type, comp.rawComponentData);
  if (comp.metadataEnabled) {
    if (comp.startTimestampLabel) {
      extracted.push({ key: comp.startTimestampLabel, type: 'datetime' });
    }
    if (comp.endTimestampLabel) {
      extracted.push({ key: comp.endTimestampLabel, type: 'datetime' });
    }
  }
  return extracted;
}

/**
 * Compute the full placeholder list for a component – zero network calls.
 *
 * Walk is edge-aware: each immediate parent edge into {@code targetId}
 * carries a {@code triggerOn} discriminator (`'CREATION'` for the
 * "On creation" port on wait-state components, `null` / `'COMPLETION'`
 * otherwise). For CREATION incoming edges from a source type registered
 * in {@code CREATION_PLACEHOLDER_DEFAULTS_BY_TYPE}, the immediate source
 * contributes its singleton creation-time placeholder set (e.g.
 * `taskUrl`, `agentSessionUrl`, `formUrl`) instead of its full output —
 * its normal outputs (form answers, agent responses) haven't resolved by
 * the time the creation child fires. Transitive ancestors of that source
 * have already resolved and contribute their full surfaces as usual.
 *
 * Targets reached via mixed edges (one COMPLETION + one CREATION) get
 * the union of both contributions, deduped by key.
 *
 * @param {string} targetId - componentId of the component being edited
 * @param {Array} components - full components array from useProjectStore
 * @param {Array} globals - global variable placeholders (fetched once at canvas init)
 * @param {Array} credentials - credential placeholders (fetched once at canvas init)
 * @returns {Array<{placeholders: Array}>} same shape as the old REST response
 */
export function computePlaceholdersForComponent(targetId, components, globals = [], credentials = []) {
  if (!targetId || !components) return [{ placeholders: [] }];

  const compMap = {};
  for (const c of components) {
    compMap[c.componentId] = c;
  }

  // Collect immediate parent edges and their per-edge trigger so the swap
  // below can fire only for edges that are actually CREATION-tagged. A
  // single source can appear multiple times if it has both COMPLETION
  // and CREATION edges into the same target; both contribute.
  const immediateParentEdges = [];
  for (const comp of components) {
    const nexts = comp.nextComponents;
    if (!Array.isArray(nexts)) continue;
    for (const nc of nexts) {
      if (nc?.componentId !== targetId) continue;
      immediateParentEdges.push({
        parentId: comp.componentId,
        triggerOn: nc?.triggerOn ?? null,
      });
    }
  }

  const placeholders = [];
  const seen = new Set();
  const pushUnique = items => {
    for (const ph of items) {
      const key = ph?.key != null ? String(ph.key) : '';
      if (!key || seen.has(key)) continue;
      seen.add(key);
      placeholders.push(ph);
    }
  };

  // Each immediate parent edge is an independent contribution; the picker
  // shows the union. CREATION and COMPLETION paths from the same parent
  // BOTH contribute (a target wired to both ports must offer field
  // answers from the COMPLETION edge AND the creation-time URL from the
  // CREATION edge). Whether the runtime resolves a particular placeholder
  // depends on which edge actually fired downstream — that's the
  // engine's job, not the picker's.
  const fullSurfaceUpstreams = new Set();
  const creationParents = new Set();

  for (const edge of immediateParentEdges) {
    const parent = compMap[edge.parentId];
    if (!parent) continue;

    if (edge.triggerOn === 'CREATION'
        && CREATION_PLACEHOLDER_DEFAULTS_BY_TYPE[parent.type]) {
      // Emit only the creation-time placeholder for the immediate source
      // (its normal outputs are unresolved at WAITING time). Ancestors of
      // the source have all resolved by the time the source entered
      // WAITING, so their full surfaces are still available on this path.
      creationParents.add(edge.parentId);
      const ancestorIds = getUpstreamComponentIds(edge.parentId, components);
      for (const id of ancestorIds) fullSurfaceUpstreams.add(id);
    } else {
      // Standard completion/null edge: the parent and its whole subtree
      // contribute full surfaces.
      fullSurfaceUpstreams.add(edge.parentId);
      const ancestorIds = getUpstreamComponentIds(edge.parentId, components);
      for (const id of ancestorIds) fullSurfaceUpstreams.add(id);
    }
  }

  // Emit full-surface upstreams first so their keys claim the dedupe slot
  // before creation placeholders. Creation keys are renameable per
  // component (taskUrl_xxx, formUrl_xxx, agentSessionUrl_xxx) so they
  // theoretically could collide with other field-answer keys, but the
  // auto-uniquify suffix keeps that vanishingly unlikely. Order is
  // cosmetic but consistent.
  for (const id of fullSurfaceUpstreams) {
    const comp = compMap[id];
    if (!comp) continue;
    pushUnique(extractedWithMetadata(comp));
  }
  for (const parentId of creationParents) {
    const parent = compMap[parentId];
    if (!parent) continue;
    pushUnique(getCreationPlaceholdersForComponent(parent));
  }

  // Include credentials for types that need them
  const target = compMap[targetId];
  if (target && TYPES_REQUIRING_SECRETS.has(target.type)) {
    pushUnique(credentials);
  }

  // Append globals
  pushUnique(globals);

  return [{ placeholders }];
}

// ---------------------------------------------------------------------------
// Compute placeholder map for all components (replaces placeholder_as_map)
// ---------------------------------------------------------------------------

/**
 * Build a map of componentId -> placeholder array for every component.
 * This replaces the backend placeholder_as_map endpoint used for output
 * hover tooltips on canvas nodes.
 *
 * @param {Array} components - full components array from useProjectStore
 * @returns {Object} { [componentId]: Array<{key, type}> }
 */
export function computeAllPlaceholdersMap(components) {
  if (!components) return {};

  const map = {};
  for (const comp of components) {
    const extracted = extractPlaceholders(comp.type, comp.rawComponentData);

    if (comp.metadataEnabled) {
      if (comp.startTimestampLabel) {
        extracted.push({ key: comp.startTimestampLabel, type: 'datetime' });
      }
      if (comp.endTimestampLabel) {
        extracted.push({ key: comp.endTimestampLabel, type: 'datetime' });
      }
    }

    map[comp.componentId] = extracted;
  }
  return map;
}
